import type { ChangeConfiguredCarFeatureQuery } from '../../graphql/documents/change-configured-car.generated';
import type { CustomizerMainQuery } from '../../graphql/documents/customizer-main.generated';
import type {
  ConfiguredCarCatalogImage,
  Maybe,
  RenderedCarImage,
  VehicleType,
} from '../../graphql/types.generated';

export interface TypedConfiguredCarCatalogImages {
  __typename?: 'TypedConfiguredCarCatalogImages';
  images: Maybe<ConfiguredCarCatalogImage[]>;
}

export interface ConfiguredCarFeature {
  __typename?: 'ConfiguredCarFeature';
  name: string;
  pr3: string;
  pr7: string;
  selected?: boolean;
  imageUrl: string;
}

export interface MappedCarIdentifier {
  model: {
    code: string;
    version: number;
    year: number;
    extensions: string[];
  };
  exteriorColor: string;
  interiorColor: string;
  salesGroup: string;
  equipmentOptions?: string[];
}

export interface MappedCar {
  idString: string;
  name?: string;
  id: MappedCarIdentifier;
  configuratorLink?: string;
  vehicleType?: Maybe<VehicleType>;
  consumption: {
    co2Class: Maybe<string>;
    consumption: Maybe<string>;
    dischargedCO2Class: Maybe<string>;
    dischargedConsumption: Maybe<string>;
    emissionCO2: Maybe<string>;
  };
  imageAssets: ImageAsset[];
  imageEnv: boolean;
  rims: ConfiguredCarFeature[];
  exteriorColors: ConfiguredCarFeature[];
  price?: number;
  carlineNameId: string;
  aveString?: string;
}

export const mapCarIdentifier = (
  id?: NonNullable<
    ChangeConfiguredCarFeatureQuery['changeConfiguredCarFeature']['configuredCar']
  >['id'],
): MappedCarIdentifier => ({
  model: {
    code: id?.model.code || '',
    version: id?.model.version || 0,
    year: id?.model.year || 0,
    extensions: id?.model.extensions || [],
  },
  exteriorColor: id?.exteriorColor || '',
  interiorColor: id?.interiorColor || '',
  salesGroup: id?.salesGroup || '',
  equipmentOptions: id?.equipmentOptions || [],
});

export type ImageAsset = {
  url: string;
  altText: string;
};

export type ImageSet = {
  assets: ImageAsset[];
  isFancy: boolean;
};

export function getImageAssets(
  media: Pick<RenderedCarImage, 'url' | 'profileId' | 'altTextMetaKeys'>[] | undefined,
): ImageSet {
  if (!media) {
    return { assets: [], isFancy: false };
  }
  // Filter images where profileId ends with '-ENV1' for environmetal images
  let envImages = media
    .filter((image) => !image.profileId.startsWith('cust') && image.profileId.endsWith('-ENV1'))
    .map((image) => ({ url: image.url, altText: image.altTextMetaKeys[1] }));

  // Filter images where profileId does not end with '-ENV1' for fancy images
  let fancyImages = media
    .filter((image) => !image.profileId.startsWith('cust') && !image.profileId.endsWith('-ENV1'))
    .map((image) => ({ url: image.url, altText: image.altTextMetaKeys[1] }));
  if (!process.env.IS_LEGACY) {
    envImages = media
      .filter((image) => image.profileId.startsWith('cust') && image.profileId.endsWith('-ENV1'))
      .map((image) => ({ url: image.url, altText: image.altTextMetaKeys[1] }));

    // Filter images where profileId does not end with '-ENV1' for fancy images
    fancyImages = media
      .filter((image) => image.profileId.startsWith('cust') && !image.profileId.endsWith('-ENV1'))
      .map((image) => ({ url: image.url, altText: image.altTextMetaKeys[1] }));
  }
  const selected = fancyImages.length > 0 ? fancyImages : envImages;

  return {
    assets: selected,
    isFancy: fancyImages.length > 0,
  };
}

export const mapConfiguredCar = (data: CustomizerMainQuery): MappedCar => {
  const {
    configuredCarByCarline: {
      carlineStructureCarline,
      media,
      catalog,
      id,
      idString: rawIdString,
      carlineIdName,
    },
  } = data;
  const idString: string = typeof rawIdString === 'string' ? rawIdString : '';
  const { carline } = data;
  const carlineNameId = carlineIdName?.id || '';
  const consumption = carline?.consumptionAndEmission?.summary;
  const vehicleType = carlineStructureCarline?.vehicleType;
  const name = carline?.name || '';
  const { assets: imageAssets, isFancy: imageEnv } = getImageAssets(media?.images);

  const rims =
    catalog?.features.rims
      .filter((rim) => !!rim.imageSets?.[0]?.images[0]?.url)
      .map(
        (rim): ConfiguredCarFeature => ({
          name: rim.name || '',
          pr3: rim.pr3,
          pr7: rim.pr7,
          selected: rim.status.selected,
          imageUrl: rim.imageSets?.[0]?.images?.[0]?.url || '',
        }),
      ) || [];

  const exteriorColors = (catalog?.features.exteriorColors ?? [])
    .filter((color) => {
      const url = color.imageSets?.[0]?.images?.[0]?.url;
      const pr3 = color.pr3 ?? '';
      const pr7 = color.pr7 ?? '';
      return !!url && pr3.length === 4 && pr7.length === 8;
    })
    // remove duplicates by imageUrl → only keep the first occurrence
    .filter((color, index, self) => {
      const url = color.imageSets?.[0]?.images?.[0]?.url;
      return index === self.findIndex((c) => c.imageSets?.[0]?.images?.[0]?.url === url);
    })
    .map(
      (color): ConfiguredCarFeature => ({
        name: color.name ?? '',
        pr3: color.pr3,
        pr7: color.pr7,
        selected: color?.status?.selected,
        imageUrl: color.imageSets?.[0]?.images?.[0]?.url || '',
      }),
    );

  const aveString: string =
    media?.ave && typeof media.ave.renderString === 'string' ? media.ave.renderString : '';

  return {
    idString,
    name,
    vehicleType,
    imageAssets,
    imageEnv,
    rims,
    exteriorColors,
    id: mapCarIdentifier(id),
    consumption: {
      co2Class: consumption?.co2Class ?? null,
      consumption: consumption?.consumption ?? null,
      dischargedCO2Class: consumption?.dischargedCO2Class ?? null,
      dischargedConsumption: consumption?.dischargedConsumption ?? null,
      emissionCO2: consumption?.emissionCO2 ?? null,
    },
    configuratorLink: carline.links?.configuratorPage?.url || undefined,
    price: carline.prices.minPrice?.value,
    carlineNameId,
    aveString,
  };
};
